import { type ReactNode } from 'react';
import { SerializedStyles } from '@emotion/react';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { Headers } from '@frontend/header';
import { Icon } from '@frontend/icons';
import { Page } from '@frontend/page';
import { useNavigate } from '@frontend/weave-router';
import { Heading, NakedButton, Text } from '@frontend/design-system';
import { URLs } from '../../constants';
import { trackingIds } from '../../tracking-ids';
import { callIntelResponsiveStyles } from '../../views/common-styles';
import { FeedbackButton } from '../feedback-button';

type Breadcrumb = {
  label: string;
  to?: string;
};

export interface SubViewHeaderProps {
  /** Page title displayed in the header */
  title: string;
  /** Subtitle text shown below the title */
  subtitle?: string;
  /** Additional subtitle content (e.g., description text) */
  subtitleExtra?: ReactNode;
  /** Breadcrumb navigation items */
  breadcrumbs: Breadcrumb[];
  /** Tracking ID for the feedback button */
  feedbackTrackingId?: string;
  /** Additional elements to render in the title row (e.g., badges) */
  titleExtra?: ReactNode;
  /** Custom styles for the header root */
  headerRootCss?: SerializedStyles;
  /** Custom styles for the header title */
  headerTitleCss?: SerializedStyles;
  /** Ref to the header element for scroll shadow handling */
  headerRef: React.RefObject<HTMLDivElement>;
  /** Custom back navigation URL */
  backUrl?: string;
}

/**
 * SubViewHeader
 *
 * A reusable header component for call intel drill-down/sub-view pages.
 * Provides consistent responsive behavior including:
 * - Scroll-based shadow effects
 * - Back button on small screens
 * - Hidden breadcrumbs on extra small screens
 * - Hidden subtitle on extra small screens
 */
export const SubViewHeader = ({
  title,
  subtitle,
  subtitleExtra,
  breadcrumbs,
  feedbackTrackingId = trackingIds.callIntel.generalFeedbackDrillDownPage,
  titleExtra,
  headerRootCss,
  headerTitleCss,
  headerRef,
  backUrl = URLs.CALL_INTEL_MAIN_PAGE,
}: SubViewHeaderProps) => {
  const navigate = useNavigate();

  const filteredBreadcrumbs = breadcrumbs.filter(({ label }) => !!label);

  return (
    <Headers.Root css={[callIntelResponsiveStyles.headerRoot, headerRootCss]} innerRef={headerRef} size='sm'>
      <Page.Header.Breadcrumbs breadcrumbs={filteredBreadcrumbs} />
      <Headers.TitleRow css={{ margin: 0 }}>
        <Headers.Title css={[callIntelResponsiveStyles.headerTitle, headerTitleCss]}>
          <NakedButton className='back-icon' trackingId={trackingIds.callIntel.drilldownBackButton}>
            <Icon name='back' onClick={() => navigate({ to: backUrl })} />
          </NakedButton>
          <Heading level={1}>{title}</Heading>
          {titleExtra}
        </Headers.Title>
        <Headers.Actions>
          <FeedbackButton productCode={Feature.CALL_INTELLIGENCE} trackingId={feedbackTrackingId} responsive />
        </Headers.Actions>
      </Headers.TitleRow>

      {(subtitle || subtitleExtra) && (
        <div css={callIntelResponsiveStyles.subtitleText}>
          {subtitle && (
            <Text size='large' as='div'>
              {subtitle}
            </Text>
          )}
          {subtitleExtra}
        </div>
      )}
    </Headers.Root>
  );
};
